import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@resideo/blueprint-react";
import { FiColumns } from "react-icons/fi";
import { RiDraggable } from "react-icons/ri";
import styles from "./ManageDevicesColumnsMenu.module.css";
import type { DeviceColumnKey } from "../ManageDevicesTable/ManageDevicesTable";

export interface ColumnOption {
  key: DeviceColumnKey;
  label: string;
  visible: boolean;
}

export interface ManageDevicesColumnsMenuProps {
  columns: ColumnOption[];
  onToggle: (key: DeviceColumnKey) => void;
  /** Called with the new left-to-right key order after a drag-and-drop. */
  onReorder?: (orderedKeys: DeviceColumnKey[]) => void;
}

export const ManageDevicesColumnsMenu = ({
  columns,
  onToggle,
  onReorder,
}: ManageDevicesColumnsMenuProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Close the popover when clicking outside or pressing Escape.
  useEffect(() => {
    if (!open) return;

    const handleClick = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const resetDrag = () => {
    setDragIndex(null);
    setOverIndex(null);
  };

  const handleDrop = (targetIndex: number) => {
    if (dragIndex === null || dragIndex === targetIndex) {
      resetDrag();
      return;
    }
    const keys = columns.map((column) => column.key);
    const [moved] = keys.splice(dragIndex, 1);
    keys.splice(targetIndex, 0, moved);
    onReorder?.(keys);
    resetDrag();
  };

  return (
    <div className={styles.container} ref={containerRef}>
      <Button
        variant="secondary"
        className={styles.iconButton}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("manageDevices.ariaLabels.columns")}
      >
        <FiColumns aria-hidden="true" />
      </Button>

      {open && (
        <div
          className={styles.menu}
          role="menu"
          aria-label={t("manageDevices.ariaLabels.toggleColumns")}
        >
          <p className={styles.menuTitle}>{t("manageDevices.columnsTitle")}</p>
          {columns.map((column, index) => (
            <label
              key={column.key}
              className={`${styles.menuItem} ${
                dragIndex === index ? styles.dragging : ""
              } ${
                overIndex === index && dragIndex !== index
                  ? styles.dragOver
                  : ""
              }`}
              draggable
              onDragStart={() => setDragIndex(index)}
              onDragOver={(event) => {
                event.preventDefault();
                setOverIndex(index);
              }}
              onDrop={(event) => {
                event.preventDefault();
                handleDrop(index);
              }}
              onDragEnd={resetDrag}
            >
              <RiDraggable className={styles.dragHandle} aria-hidden="true" />
              <input
                type="checkbox"
                className={styles.checkbox}
                checked={column.visible}
                onChange={() => onToggle(column.key)}
              />
              <span className={styles.itemLabel}>{t(column.label)}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

export default ManageDevicesColumnsMenu;
